import {Controller, useForm} from "react-hook-form";
import {CustomInput} from "../../../theme/styles";
import {
    Button, Form, FormGroup, ModalFooter, Label, Row, Col, FormFeedback, Spinner, List, ListInlineItem,
} from "reactstrap";
import {memo, useEffect, useState} from "react";
import {GoogleMap, Marker, StandaloneSearchBox} from "@react-google-maps/api";
import {lgaByState, nigerianStates} from "../../../api/geographics";
import NaijaStates from 'naija-state-local-government'


export const AddUserAddressForm = memo(({defaultValues = {}, onSubmit, loading, responseErrors = []}) => {
    const required = v => !!v?.trim();

    const {control, watch, handleSubmit, setValue, formState: {errors}} = useForm({
        mode: "onChange", defaultValues: {
            firstName: "",
            lastName: "",
            state: "",
            city: "",
            LGA: "",
            street: "",
            longitude: "",
            latitude: "",
            post_code: 0,
            phone: ""
        }
    });

    const [coords, setCoords] = useState({lat: 6.5244, lng: 3.3792});
    const [locationSelected, setLocationSelected] = useState(false);
    const [searchBox, setSearchBox] = useState(null);

    const selectedState = watch("state");

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const handlePlaceChanged = () => {
        const places = searchBox.getPlaces();
        if (places && places.length > 0) {
            const place = places[0];
            const location = place.geometry.location;
            const lat = location.lat();
            const lng = location.lng();
            setCoords({lat, lng});
            setValue("latitude", lat);
            setValue("longitude", lng);

            const components = place.address_components || [];
            const getComponent = (type) => components.find((c) => c.types.includes(type))?.long_name || "";
            const state = getComponent("administrative_area_level_1");

            setValue("state", state);
            setValue("city", getComponent("locality") || getComponent("administrative_area_level_2"));
            setValue("LGA", getComponent("sublocality") || getComponent("administrative_area_level_3"));
            setValue("street", place.formatted_address);

            const postalCode = getComponent("postal_code");
            if (postalCode) setValue("post_code", postalCode);

            setLocationSelected(true)
        }
    };

    const handleStateChange = (value) => {
        setValue("state", value);
        setValue("LGA", "");
    };

    const handleMapClick = (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setCoords({lat, lng});
        setValue("latitude", lat);
        setValue("longitude", lng);

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({location: {lat, lng}}, (results, status) => {
            if (status === "OK" && results[0]) {
                const place = results[0];
                const components = place.address_components || [];
                const getComponent = (type) => components.find((c) => c.types.includes(type))?.long_name || "";
                const state = getComponent("administrative_area_level_1");

                setValue("state", state);
                setValue("city", getComponent("locality") || getComponent("administrative_area_level_2"));
                setValue("LGA", getComponent("sublocality") || getComponent("administrative_area_level_3"));
                setValue("street", place.formatted_address);

                const postalCode = getComponent("postal_code");
                if (postalCode) setValue("post_code", postalCode);

                setLocationSelected(true)
            }
        });
    };

    const naijaStates = NaijaStates.all()

    return <>
        <Form onSubmit={handleSubmit(onSubmit)}>
            <div>
                <FormGroup>
                    <Label>Choose Your Delivery Location <span className="required">*</span></Label>
                    <div style={{marginTop: "10px"}}>
                        {typeof window !== "undefined" && window.google && (
                            <div style={{height: "320px", width: "100%", position: "relative", overflow: "hidden"}}>
                                <StandaloneSearchBox onLoad={setSearchBox}
                                                     bounds={{
                                                         north: 13.9,
                                                         south: 4.0,
                                                         east: 14.7,
                                                         west: 2.7
                                                     }}
                                                     onPlacesChanged={handlePlaceChanged}>
                                    <CustomInput
                                        type="text"
                                        className={"mb-md-3 mb-2"}
                                        placeholder="Search your delivery location"
                                        onKeyDown={handleKeyDown}
                                    />
                                </StandaloneSearchBox>
                                {(errors.deliveryLocation) && (<FormFeedback
                                    className="d-block">{errors?.deliveryLocation?.message}</FormFeedback>)}
                                <GoogleMap
                                    mapContainerStyle={{
                                        width: "100%", height: "100%", borderRadius: "8px",
                                    }}
                                    options={{
                                        mapTypeControl: false,
                                        streetViewControl: false,
                                        fullscreenControl: false,
                                        zoomControl: true,
                                        clickableIcons: false,
                                        gestureHandling: "greedy"
                                    }}
                                    center={coords}
                                    zoom={13}
                                    onClick={handleMapClick}
                                >
                                    <Marker position={coords} draggable onDragEnd={handleMapClick}/>
                                </GoogleMap>
                            </div>)}
                    </div>
                </FormGroup>

                <Controller name={"latitude"} control={control} rules={{required: "Latitude is required"}}
                            render={({field}) => (<input type="hidden" {...field} />)}/>
                <Controller name={"longitude"} control={control} rules={{required: "Longitude is required"}}
                            render={({field}) => (<input type="hidden" {...field} />)}/>

                {(errors.latitude) && (<p className={"invalid-error"}>{errors.latitude.message}</p>)}
                {(errors.longitude) && (<p className={"invalid-error"}>{errors.longitude.message}</p>)}

                {locationSelected && (<>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="firstName">First Name</Label>
                                <Controller
                                    name={"firstName"}
                                    control={control}
                                    rules={{
                                        required: true,
                                        validate: required,
                                        minLength: {value: 2, message: "Min length is 2 characters"}
                                    }}
                                    render={({field}) => (<CustomInput
                                        {...field}
                                        id={"firstName"}
                                        type="text"
                                        invalid={!!errors.firstName}
                                        placeholder="Enter your first name"
                                    />)}
                                />
                                {errors.firstName?.message && (
                                    <FormFeedback>{errors.firstName?.message}</FormFeedback>)}
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for="lastName">Last Name</Label>
                                <Controller
                                    name={"lastName"}
                                    control={control}
                                    rules={{
                                        required: true,
                                        validate: required,
                                        minLength: {value: 2, message: "Min length is 2 characters"}
                                    }}
                                    render={({field}) => (<CustomInput
                                        {...field}
                                        id={"lastName"}
                                        type="text"
                                        invalid={!!errors.lastName}
                                        placeholder="Enter your last name"
                                    />)}
                                />
                                {errors.lastName?.message && (<FormFeedback>{errors.lastName?.message}</FormFeedback>)}
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup>
                        <Label for="phone">Phone Number *</Label>
                        <Controller
                            name={"phone"}
                            control={control}
                            rules={{
                                required: true,
                                validate: required,
                                minLength: { value: 11, message: "Min length is 11 characters" },
                                pattern: {
                                    value: /^[0-9+;]+$/,
                                    message: "Phone number can only contain digits, '+' and ';'",
                                },
                            }}
                            render={({field}) => (<CustomInput
                                {...field}
                                id={"phone"}
                                type="tel"
                                invalid={!!errors.phone}
                                placeholder="Enter contact phone number"
                            />)}
                        />
                        {errors.phone?.message && (<FormFeedback>{errors.phone?.message}</FormFeedback>)}
                    </FormGroup>

                    <FormGroup>
                        <Label>State/Province</Label>
                        <Controller
                            control={control}
                            name={"state"}
                            rules={{required: true, validate: required}}
                            render={({field}) => (<CustomInput
                                {...field}
                                onChange={(e) => {
                                    field.onChange(e);
                                    handleStateChange(e.target.value);
                                }}
                                invalid={!!errors.state}
                                placeholder="Select your state or province"
                                type="select"
                            >
                                <option value="">Select State</option>
                                {naijaStates.flatMap(d=>d?.state).map((state) => (
                                    <option key={state} value={state}>{state}</option>))}
                            </CustomInput>)}
                        />
                        {errors.state?.message && (<FormFeedback>{errors.state?.message}</FormFeedback>)}
                    </FormGroup>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>City</Label>
                                <Controller
                                    control={control}
                                    rules={{required: true, validate: required}}
                                    render={({field}) => (<CustomInput
                                        {...field}
                                        invalid={!!errors.city}
                                        placeholder="Select City"
                                    />)}
                                    name={"city"}
                                />
                                {errors.city?.message && (<FormFeedback>{errors.city?.message}</FormFeedback>)}
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>LGA</Label>
                                <Controller
                                    rules={{required: true, validate: required}}
                                    name={"LGA"}
                                    control={control}
                                    render={({field}) => (<CustomInput
                                        {...field}
                                        type={"select"}
                                        invalid={!!errors.LGA}
                                        placeholder="Select LGA"
                                        disabled={!selectedState}
                                    >
                                        <option value="">Select LGA</option>
                                        {selectedState && NaijaStates.lgas(selectedState)?.lgas?.map((lga) => (
                                            <option key={lga} value={lga}>{lga}</option>
                                        ))}
                                    </CustomInput>)}
                                />
                                {errors.LGA?.message && (<FormFeedback>{errors.LGA?.message}</FormFeedback>)}
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup>
                        <Label>Street Address</Label>
                        <Controller
                            rules={{required: true, validate: required}}
                            control={control}
                            render={({field}) => (<CustomInput
                                {...field}
                                invalid={!!errors.street}
                                type="textarea"
                                rows="7"
                                placeholder="Enter street address"
                            />)}
                            name={"street"}
                        />
                        {errors.street?.message && (<FormFeedback>{errors.street?.message}</FormFeedback>)}
                    </FormGroup>
                </>)}
            </div>

            {responseErrors.length > 0 && (<List type={"inline"}>
                {responseErrors.map((error, index) => (
                    <ListInlineItem className={"tw-text-red-500"} key={index}>{error}</ListInlineItem>))}
            </List>)}

            <ModalFooter>
                <Button  type="submit" color="success">
                    <span>Save Address</span>
                    {loading ? (<Spinner size="sm" className="ms-2"/>) : undefined}
                </Button>
            </ModalFooter>
        </Form>
    </>
})